import {
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect
} from "react";

import {
    AnalyticsEventPayload,
    useAnalyticsEvents
} from "@atlaskit/analytics-next";
import FeatureGates from "@atlassian/feature-gate-js-client";
import { useFireCrossFlowAnalyticsEvents } from "@atlassian/surface-analytics";
import { CrossFlowViewedScreenNames } from "@atlassian/surface-analytics-specs";
import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";
import {
    Journeys,
    TargetType,
    useCrossFlow
} from "@atlassiansox/cross-flow-support";

import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { useAnalyticsClient } from "../../../analytics/wac-analytics/analytics-client";

const productRolloutExperimentMap: Partial<Record<ProductKeys, string>> = {
    [ProductKeys.CONFLUENCE]: "cross-flow_frontend_for_confluence_product_tour",
    [ProductKeys.JIRA_SOFTWARE]: "cross-flow_frontend_for_jira",
    [ProductKeys.JIRA_PRODUCT_DISCOVERY]:
        "cross-flow_frontend_for_jpd_product_tour"
};

const getRolloutFlagValue = (
    productKey: ProductKeys,
    fireExperimentExposure: boolean
) => {
    const experimentKey = productRolloutExperimentMap[productKey];
    if (!experimentKey) {
        return "not-enrolled";
    }
    return FeatureGates.getExperimentValue(
        experimentKey,
        "cohort",
        "not-enrolled",
        {
            fireExperimentExposure
        }
    );
};
interface CrossFlowFrontendConfig {
    product: ProductKeys;
    pageName: CrossFlowViewedScreenNames;
}

type CrossFlowExtensionOptions = {
    edition?: Editions;
};

type OpenCrossFlowFunction = (event: React.MouseEvent) => Promise<void>;

type CrossFlowFrontendContext = {
    isCrossFlowEnabled: boolean;
    config?: CrossFlowFrontendConfig;
};

const CrossFlowFrontendContext = createContext<CrossFlowFrontendContext>({
    isCrossFlowEnabled: false
});

const useCrossFlowFrontendContext = ({
    pageName,
    product
}: CrossFlowFrontendConfig): CrossFlowFrontendContext => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();

    const { fireViewedScreenEvent } = useFireCrossFlowAnalyticsEvents();
    const { isUserLoggedIn } = useLoggedInUser();
    const crossFlow = useCrossFlow();
    const crossFlowFrontendFlagValue = getRolloutFlagValue(product, false);
    const isEnrolled = ["variation", "control"].includes(
        crossFlowFrontendFlagValue
    );

    const fireEvent = useCallback(
        (eventPayload: AnalyticsEventPayload) => {
            analyticsClient &&
                createAnalyticsEvent({
                    ...eventPayload,
                    attributes: {
                        product,
                        pageName,
                        crossFlowFrontendFlagValue,
                        isUserLoggedIn,
                        isCrossFlowEnabled: crossFlow?.isEnabled,
                        isInvalidConfig: !product || !pageName
                    }
                }).fire();
        },
        [
            analyticsClient,
            createAnalyticsEvent,
            crossFlow?.isEnabled,
            crossFlowFrontendFlagValue,
            isUserLoggedIn,
            pageName,
            product
        ]
    );

    useEffect(() => {
        analyticsClient &&
            fireEvent({
                action: "viewed",
                actionSubject: "crossFlowButton",
                eventType: "ui"
            });
    }, [analyticsClient, fireEvent]);

    useEffect(() => {
        fireViewedScreenEvent(pageName, {
            recommendedProductIds: [product]
        });
    }, [fireViewedScreenEvent, pageName, product]);

    if (
        !product ||
        !pageName ||
        !crossFlow?.isEnabled ||
        !isUserLoggedIn ||
        !isEnrolled
    ) {
        fireEvent({
            action: "disabled",
            actionSubject: "crossFlowFrontend",
            eventType: "operational"
        });
        return {
            isCrossFlowEnabled: false
        };
    }

    return {
        isCrossFlowEnabled: true
    };
};

export const CrossFlowFrontendProvider = ({
    children,
    ...config
}: PropsWithChildren<CrossFlowFrontendConfig>) => {
    const { isCrossFlowEnabled } = useCrossFlowFrontendContext(config);
    return (
        <CrossFlowFrontendContext.Provider
            value={{
                isCrossFlowEnabled,
                config
            }}
        >
            {children}
        </CrossFlowFrontendContext.Provider>
    );
};

export const useCrossFlowFrontend = (
    fallbackHref: string,
    options?: CrossFlowExtensionOptions
) => {
    const { isCrossFlowEnabled, config } = useContext(CrossFlowFrontendContext);
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const crossFlow = useCrossFlow();

    const fireEvent = useCallback(
        (
            eventPayload: AnalyticsEventPayload,
            attributes: AnalyticsEventPayload["attributes"] = {}
        ) => {
            analyticsClient &&
                createAnalyticsEvent({
                    ...eventPayload,
                    attributes: {
                        fallbackHref,
                        options,
                        ...attributes
                    }
                }).fire();
        },
        [analyticsClient, createAnalyticsEvent, fallbackHref, options]
    );

    const openCrossFlow: OpenCrossFlowFunction = useCallback(
        async (event) => {
            try {
                if (!config) {
                    return;
                }
                fireEvent({
                    action: "clicked",
                    actionSubject: "crossFlowButton",
                    eventType: "track"
                });
                const isCrossFlowRolloutVariationCohort =
                    getRolloutFlagValue(config.product, true) === "variation";

                if (
                    !isCrossFlowRolloutVariationCohort ||
                    !crossFlow.isEnabled
                ) {
                    return;
                }
                if (event) {
                    event.preventDefault();
                }

                fireEvent({
                    action: "successful",
                    actionSubject: "crossFlowRequest",
                    eventType: "operational"
                });

                await crossFlow.api.open({
                    journey: Journeys.GET_STARTED,
                    targetProduct: config.product as TargetType,
                    sourceComponent: config.pageName,
                    sourceContext: "crossFlowButton",
                    extensions: {
                        edition: options?.edition
                    }
                });
            } catch (error) {
                const errorData = error as Error;
                fireEvent(
                    {
                        action: "failed",
                        actionSubject: "crossFlowRequest",
                        eventType: "operational"
                    },
                    {
                        errorMessage: errorData?.message,
                        errorStack: errorData?.stack
                    }
                );
                if (typeof fallbackHref !== "string") {
                    fireEvent(
                        {
                            action: "failed",
                            actionSubject: "crossFlowFallbackRedirectAttempt",
                            eventType: "operational"
                        },
                        {
                            errorMessage: "Fallback href is not a string"
                        }
                    );
                    return;
                }
                fireEvent({
                    action: "successful",
                    actionSubject: "crossFlowFallbackRedirectAttempt",
                    eventType: "operational"
                });
                window.location.assign(fallbackHref);
            }
        },
        [config, fireEvent, crossFlow, options?.edition, fallbackHref]
    );

    if (!isCrossFlowEnabled || !config) {
        return { openCrossFlow: undefined };
    }
    return {
        openCrossFlow
    };
};
